import { useState } from 'react';
import type { JSX } from 'react';

import { extractErrorMessage } from 'shared/utils/extractErrorMessage';

import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import {
  useCreateIntegrationMutation,
  useGetCredentialsQuery,
  useUpdateIntegrationMutation,
} from '../api/integrations';
import { ID_TEXT_CLASS } from '../lib/id';
import { PROVIDER_KEYS } from '../model/integration';
import type { Integration } from '../model/integration';
import FormDialog, { Field } from './FormDialog';

const NO_CREDENTIAL = 'none';

interface IntegrationFormDialogProps {
  projectId: number;
  /** Null creates a new one */
  integration: Integration | null;
  onClose: () => void;
  onSaved: (integration: Integration) => void;
}

const IntegrationFormDialog = ({
  projectId,
  integration,
  onClose,
  onSaved,
}: IntegrationFormDialogProps): JSX.Element => {
  const [provider, setProvider] = useState(integration?.provider ?? PROVIDER_KEYS[0]);
  const [label, setLabel] = useState(integration?.label ?? '');
  const [credentialId, setCredentialId] = useState(integration?.credentialId?.toString() ?? NO_CREDENTIAL);
  const [externalAccountId, setExternalAccountId] = useState(integration?.externalAccountId ?? '');
  const [error, setError] = useState<string | null>(null);

  const { data: credentials = [] } = useGetCredentialsQuery(provider);
  const [createIntegration, { isLoading: isCreating }] = useCreateIntegrationMutation();
  const [updateIntegration, { isLoading: isUpdating }] = useUpdateIntegrationMutation();

  const handleSubmit = async (): Promise<void> => {
    setError(null);
    const fields = {
      label: label.trim(),
      credentialId: credentialId === NO_CREDENTIAL ? null : Number(credentialId),
      externalAccountId: externalAccountId.trim() || null,
    };
    try {
      const saved = integration
        ? await updateIntegration({ id: integration.id, body: fields }).unwrap()
        : await createIntegration({ projectId, provider, ...fields }).unwrap();
      onSaved(saved);
    } catch (err) {
      setError(extractErrorMessage(err, 'No se pudo guardar la integración'));
    }
  };

  return (
    <FormDialog
      title={integration ? `Editar integración #${integration.id}` : 'Nueva integración'}
      submitLabel={integration ? 'Guardar' : 'Crear'}
      isSaving={isCreating || isUpdating}
      error={error}
      onClose={onClose}
      onSubmit={handleSubmit}
    >
      <Field label="Provider" hint={integration ? 'El provider no se puede cambiar.' : undefined}>
        <Select
          value={provider}
          disabled={!!integration}
          onValueChange={(value) => {
            setProvider(value);
            setCredentialId(NO_CREDENTIAL);
          }}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PROVIDER_KEYS.map((key) => (
              <SelectItem key={key} value={key}>
                {key}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Nombre" htmlFor="integration-label">
        <Input id="integration-label" value={label} onChange={(e) => setLabel(e.target.value)} required />
      </Field>
      <Field label="Credencial" hint="Las credenciales se crean en la sección Credenciales, al final de la página.">
        <Select value={credentialId} onValueChange={setCredentialId}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_CREDENTIAL}>Sin credencial</SelectItem>
            {credentials.map((credential) => (
              <SelectItem key={credential.id} value={credential.id.toString()}>
                {credential.label} <span className={ID_TEXT_CLASS}>#{credential.id}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field
        label="ID de cuenta externa"
        htmlFor="integration-external-account"
        hint="Opcional. Identifica la cuenta en el sistema externo (por ejemplo, el pipeline o portal)."
      >
        <Input
          id="integration-external-account"
          value={externalAccountId}
          onChange={(e) => setExternalAccountId(e.target.value)}
        />
      </Field>
    </FormDialog>
  );
};

export default IntegrationFormDialog;
